import { safeLocalStorage } from '@/lib/safeStorage';
import {useEffect} from 'react';
import {QueryClient} from '@tanstack/react-query';
import {refreshUserTokens} from './routes/auth';
import {getIdToken, getRefreshToken} from './auth';
import {jwtDecode} from 'jwt-decode';
import {STORAGE_KEYS} from '../constants';
// Import the generic API call function and dependencies
import {ApiError, getApiUrl, handleSessionExpired} from './core';

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

// Subscribe to token refresh completion
function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

// Notify all subscribers when refresh is complete
function onTokenRefreshed(token: string | null) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

// Check if token will expire within the next 5 minutes
export function isTokenExpiringSoon(token: string): boolean {
  try {
    const decoded = jwtDecode<any>(token);
    const expiryTime = decoded.exp * 1000;
    // A token that never says when it expires cannot be trusted to still be good — without this
    // the comparison below is NaN, which reads as "plenty of time left" and never stops being so.
    if (!Number.isFinite(expiryTime)) return true;
    const now = Date.now();
    const fiveMinutesFromNow = now + (5 * 60 * 1000);
    return expiryTime < fiveMinutesFromNow;
  } catch {
    return true; // If we can't decode, assume it's expiring
  }
}

// Refresh token if needed (or when forced due to missing claims)
function isMissingOrgClaimsError(message: string): boolean {
  return (
    message.includes('Organization ID not found in token') ||
    message.includes('missing organization claims')
  );
}

export async function refreshTokenIfNeeded(forceRefresh = false): Promise<string | null> {
  const idToken = safeLocalStorage.getItem(STORAGE_KEYS.ID_TOKEN);
  // A platform-operator "view as" session must never spend a leftover refresh token from a
  // previous login — that refresh would mint the operator's home-org JWT and bounce the
  // company they were looking at (RUT-3775). View-as is time-boxed by having no refresh.
  const refreshToken = safeLocalStorage.getItem(STORAGE_KEYS.VIEW_AS) ? null : getRefreshToken();

  if (!refreshToken) {
    // A token with time left on it is good as it stands, even with nothing to renew it later.
    // That case is the platform-operator "view as" session (RUT-3105), handed an id/access pair
    // and NO refresh token on purpose, since that absence is what time-boxes it. Reporting "no
    // session" for those made callers refuse to send the request at all, so a view-as session
    // could not make a single API call — the org never loaded and the app bounced to
    // /organization-setup forever. `forceRefresh` (a 403) still declines to reuse the token.
    if (!forceRefresh && idToken && !isTokenExpiringSoon(idToken)) {
      return idToken;
    }

    // Within five minutes of expiry — or unreadable — with no way to renew, so close the session
    // properly. Left alone, a view-as session spent its last stretch still looking signed in while
    // every request failed on its own; it has to close, not decay. The expiry test has to stay:
    // a forced refresh (a 403) arrives here with a perfectly good token, and a permission error
    // must not sign the operator out.
    if (idToken && isTokenExpiringSoon(idToken)) {
      console.log('[refreshTokenIfNeeded] Session token spent with no way to renew — ending session');
      handleSessionExpired();
      return null;
    }

    console.log('[refreshTokenIfNeeded] No refresh token available');
    return null;
  }

  // For 403 errors (missing org claims), we always need to refresh
  // even if the token isn't expiring soon
  if (!forceRefresh && idToken && !isTokenExpiringSoon(idToken)) {
    console.log('[refreshTokenIfNeeded] Token not expiring soon, skipping refresh');
    return idToken;
  }

  // If already refreshing, wait for the result
  if (isRefreshing) {
    console.log('[refreshTokenIfNeeded] Already refreshing, waiting for result...');
    return new Promise((resolve) => {
      subscribeTokenRefresh((token) => {
        resolve(token);
      });
    });
  }

  // Start refreshing
  isRefreshing = true;
  console.log('[refreshTokenIfNeeded] Starting token refresh (forced: ' + forceRefresh + ')...');

  try {
    const success = await refreshUserTokens();
    if (success) {
      const newToken = getIdToken();
      console.log('[refreshTokenIfNeeded] Token refresh successful, new token retrieved');
      onTokenRefreshed(newToken);
      return newToken;
    } else {
      console.log('[refreshTokenIfNeeded] Token refresh failed - invalid/expired refresh token');
      onTokenRefreshed(null);
      // Clear all tokens and trigger session expired
      handleSessionExpired();
      return null;
    }
  } catch (error: any) {
    console.error('[refreshTokenIfNeeded] Token refresh failed:', error);
    // If refresh token is invalid, clear session
    if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('NotAuthorizedException')) {
      console.log('[refreshTokenIfNeeded] Refresh token invalid - clearing session');
      handleSessionExpired();
    }
    onTokenRefreshed(null);
    return null;
  } finally {
    isRefreshing = false;
  }
}

async function makeApiCallDirect<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  body?: any,
  customHeaders?: Record<string, string>,
): Promise<T> {
  const token = getIdToken();

  if (!token) {
    console.log('[makeApiCallDirect] No token found in localStorage');
    handleSessionExpired();
    throw new Error('No token available');
  }

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...customHeaders,
    };

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      fetchOptions.body = JSON.stringify(body);
    }

    const url = `${getApiUrl()}${endpoint}`;
    console.log(`[makeApiCallDirect] ${method} ${url}`, body ? { body } : '');
    
    const res = await fetch(url, fetchOptions);

    // Check if response is successful (2xx status codes)
    if (res.status < 200 || res.status >= 300) {
      console.log(`[makeApiCallDirect] Got ${res.status} response for ${method} ${endpoint}`);
      const errorData = await res.json().catch(() => ({}) as any);

      // For 403 errors specifically related to missing org claims, we want to trigger refresh
      if (res.status === 403) {
        console.log(`[makeApiCallDirect] 403 error message: ${errorData.message}`);
      }
      // Specific error codes for duplicate emails
      const statusInfo = res.status === 403 ? '403 Forbidden: ' : res.status === 401 ? '401 Unauthorized: ' : '';
      const envelope = errorData.error;
      const isEnvelopeObject = envelope && typeof envelope === 'object';
      const errorMessage =
        (isEnvelopeObject && envelope.message) ||
        (typeof envelope === 'string' && envelope) ||
        errorData.message ||
        'Something went wrong. Please try again.';
      // Preserve the machine-readable parts of the error envelope (RUT-2643)
      // so callers can branch on e.g. `error.details.reason` while
      // message-only consumers keep the exact same message as before.
      throw new ApiError(statusInfo + errorMessage, {
        status: res.status,
        code: isEnvelopeObject ? envelope.code : undefined,
        details: isEnvelopeObject ? envelope.details : undefined,
      });
    }

    // Handle 204 No Content responses
    if (res.status === 204) {
      console.log(`[makeApiCallDirect] Response: No Content (204)`);
      return undefined as T;
    }

    const responseData = await res.json();
    console.log(`[makeApiCallDirect] Response:`, responseData);
    return responseData;
  } catch (error: any) {
    // Handle 401 Unauthorized errors (session expired)
    if (error.message && (
      error.message.includes('401') ||
      error.message.includes('Unauthorized') ||
      error.message.includes('Invalid or expired') ||
      error.message.includes('Token expired')
    )) {
      console.log(`[makeApiCallDirect] Session expired`);
      throw error; // Let the calling function handle the retry
    }

    // Re-throw other errors as-is
    throw error;
  }
}

export async function makeApiCallWithRefresh<T = any>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  body?: any,
  skipRefresh = false,
  customHeaders?: Record<string, string>,
): Promise<T> {
  if (!skipRefresh) {
    const token = await refreshTokenIfNeeded(false);
    if (!token) {
      throw new Error('Authentication required');
    }
  }
  
  try {
    return await makeApiCallDirect<T>(method, endpoint, body, customHeaders);
  } catch (error: any) {
    console.log(`[makeApiCallWithRefresh] Caught error: ${error.message}`);
    console.log(`[makeApiCallWithRefresh] Skip refresh: ${skipRefresh}`);
    
    // Retry once on an expired token (401), or on a 403 that is specifically missing org
    // claims. Any other 403 — view-as is read-only, missing permission — must not refresh:
    // a leftover refresh token would replace this company with the operator's home org
    // (RUT-3775).
    const shouldRefresh = !skipRefresh && error.message && (
      error.message.includes('401') ||
      error.message.includes('Unauthorized') ||
      error.message.includes('Token expired') ||
      isMissingOrgClaimsError(error.message)
    );
    
    console.log(`[makeApiCallWithRefresh] Should refresh: ${shouldRefresh}`);
    
    if (shouldRefresh) {
      console.log('[makeApiCallWithRefresh] Got authentication/authorization error, attempting token refresh...');
      const newToken = await refreshTokenIfNeeded(isMissingOrgClaimsError(error.message));
      console.log(`[makeApiCallWithRefresh] Token refresh result: ${newToken ? 'success' : 'failed'}`);
      
      if (newToken) {
        console.log('[makeApiCallWithRefresh] Retrying request with refreshed token...');
        return await makeApiCallDirect<T>(method, endpoint, body, customHeaders);
      }
    }
    
    // Re-throw the error if refresh didn't help or wasn't attempted
    throw error;
  }
}

// Configure TanStack Query to use our token refresh logic
export function configureQueryClient(queryClient: QueryClient) {
  // Set default query retry logic
  queryClient.setDefaultOptions({
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors except 401
        if (error?.message && error.message.includes('401')) {
          return failureCount < 1; // Retry once for 401 errors
        }
        if (error?.message && /4\d\d/.test(error.message)) {
          return false; // Don't retry other 4xx errors
        }
        return failureCount < 3; // Default retry logic for other errors
      },
      staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
      gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    },
    mutations: {
      retry: false, // Don't retry mutations by default
    },
  });
}

// Hook to refresh tokens periodically
export function useTokenRefresh() {
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const scheduleNextRefresh = () => {
      const idToken = safeLocalStorage.getItem(STORAGE_KEYS.ID_TOKEN);
      if (!idToken) return;

      try {
        const decoded = jwtDecode<any>(idToken);
        const expiryTime = decoded.exp * 1000;
        const now = Date.now();

        // Schedule refresh 5 minutes before expiry
        const refreshTime = expiryTime - (5 * 60 * 1000);
        const timeUntilRefresh = refreshTime - now;

        if (timeUntilRefresh > 0) {
          console.log(`[useTokenRefresh] Token expires at ${new Date(expiryTime).toLocaleString()}`);
          console.log(`[useTokenRefresh] Scheduling refresh in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);

          timeoutId = setTimeout(async () => {
            console.log('[useTokenRefresh] Token expiring soon, refreshing...');
            const success = await refreshTokenIfNeeded();

            // If refresh was successful, schedule the next one
            if (success) {
              scheduleNextRefresh();
            }
          }, timeUntilRefresh);
        } else {
          // Token is already expired or expiring very soon, refresh immediately
          console.log('[useTokenRefresh] Token expired or expiring soon, refreshing immediately...');
          refreshTokenIfNeeded().then((success) => {
            if (success) {
              scheduleNextRefresh();
            }
          });
        }
      } catch (error) {
        console.error('[useTokenRefresh] Error decoding token:', error);
      }
    };

    // Handle page visibility changes (mobile resume from background)
    // Mobile browsers suspend JS timers when backgrounded, so scheduled
    // refreshes won't fire. Check and refresh when page becomes visible.
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[useTokenRefresh] Page became visible, checking token...');
        const idToken = safeLocalStorage.getItem(STORAGE_KEYS.ID_TOKEN);

        if (idToken && isTokenExpiringSoon(idToken)) {
          console.log('[useTokenRefresh] Token expired/expiring after resume, refreshing...');
          const success = await refreshTokenIfNeeded();
          if (success) {
            // Reschedule with the new token
            clearTimeout(timeoutId);
            scheduleNextRefresh();
          }
        }
      }
    };

    // Schedule the first refresh
    scheduleNextRefresh();

    // Also schedule refresh when tokens are updated (e.g., after login)
    const handleTokensUpdated = () => {
      clearTimeout(timeoutId);
      scheduleNextRefresh();
    };

    window.addEventListener('tokensUpdated', handleTokensUpdated);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Clean up on unmount
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('tokensUpdated', handleTokensUpdated);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
